/**
 * IndividualReportPage - 個人分析レポートページ（新しいウィンドウ用）
 * URLパラメータからuserIdを取得し、DesignReportViewで表示
 * font-sizeベースのレスポンシブ対応（オリジナルデザインと同じ仕組み）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { DesignReportView, ReportScores } from './DesignReportView';
import { Gb74PersonalReport } from './Gb74PersonalReport';
import { unwrapGb74Scores } from '../utils/gb74Report';
import { analysisApi } from '../api/analysisApi';
import type { AnalysisResult } from '../types';

/**
 * ユーザー名からイニシャルを生成
 */
const getInitials = (name: string): string => {
  const parts = name.split(/[\s　]+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/**
 * 日付をフォーマット
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\//g, '.');
};

/**
 * スコアをReportScores形式に変換（2で割った値）
 */
const convertToReportScores = (scores: Record<string, number>): ReportScores => {
  return {
    A: Math.round((scores['A'] || 0) / 2),
    B: Math.round((scores['B'] || 0) / 2),
    C: Math.round((scores['C'] || 0) / 2),
    D: Math.round((scores['D'] || 0) / 2),
  };
};

export const IndividualReportPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchResult = useCallback(async () => {
    if (!userId) {
      setError('ユーザーIDが指定されていません');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await analysisApi.getLatestResult(userId);
      setResult(data);
    } catch (err) {
      console.error('Failed to fetch result:', err);
      setError('分析結果の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchResult();
  }, [fetchResult]);

  // ウィンドウタイトルを設定
  useEffect(() => {
    if (result) {
      const displayName = result.userName || userId;
      document.title = `分析レポート - ${displayName}`;
    }
  }, [result, userId]);

  // html要素にレポート専用クラスを追加（rem単位の計算を正しくするため）
  useEffect(() => {
    document.documentElement.classList.add('design-report-active');
    return () => {
      document.documentElement.classList.remove('design-report-active');
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!result) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">分析結果が見つかりません</Typography>
      </Box>
    );
  }

  const displayName = result.userName || userId || '';

  // gb74形式なら最新DB(desc)結線済みの Gb74PersonalReport を表示。
  // 非gb74(旧データ)は従来の DesignReportView にフォールバック。
  const gb74Scores = unwrapGb74Scores(result.scores?.scores);
  if (gb74Scores) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', overflow: 'auto', py: 3 }}>
        <Box sx={{ maxWidth: 820, mx: 'auto', px: 2 }}>
          <Gb74PersonalReport
            scores={result.scores.scores}
            userName={displayName}
            answerDate={formatDate(result.createdAt)}
            companyName={result.companyName || undefined}
            departmentName={result.departmentName || undefined}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        overflow: 'auto',
      }}
    >
      {/* c-sec-report構造を再現 (旧データ向けフォールバック) */}
      <section className="c-sec-report" style={{ marginTop: '1.7rem', paddingBottom: '11rem' }}>
        <div className="inner" style={{ marginInline: 'auto', width: '89.5833333333%', maxWidth: '129rem' }}>
          <DesignReportView
            profile={{
              name: displayName,
              initials: getInitials(displayName),
              date: formatDate(result.createdAt),
            }}
            scores={convertToReportScores(result.scores.scores)}
            content={result.patternDescription ? {
              thinkingType: result.patternName || '',
              temperamentType: '',
              thoughtTheme: '',
              thoughtDescription: result.patternDescription,
              actionTheme: '',
              actionDescription: '',
            } : undefined}
          />
        </div>
      </section>
    </Box>
  );
};

export default IndividualReportPage;
